import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Security, Person, Save, VpnKey, PhotoCamera } from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { adminAPI } from "../../services/api";
import { toast } from "react-toastify";

const AdminProfile = () => {
  const { user, updateUser } = useAuth();
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: ""
  });
  const [photoLoading, setPhotoLoading] = useState(false);

  // Helper to get profile picture URL
  const getProfilePicUrl = () => {
    if (!user?.profilePicture) return null;
    if (typeof user.profilePicture === "string") return user.profilePicture;
    if (user.profilePicture?.url) return user.profilePicture.url;
    return null;
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 1.5 * 1024 * 1024) {
      toast.error("Image too large. Max 1.5MB.");
      return;
    }
    setPhotoLoading(true);
    try {
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      const imageDataUrl = await base64Promise;
      const res = await adminAPI.uploadUserAvatar(user._id || user.id, { image: imageDataUrl });
      if (typeof updateUser === 'function') updateUser(res.data.user);
      toast.success("Profile photo updated!");
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setPhotoLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      toast.error("New passwords do not match!");
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/users/update-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('hostly_admin_token')}`
        },
        body: JSON.stringify({
          userId: user?._id || user?.id,
          currentPassword: passwords.current,
          newPassword: passwords.new
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success("Security Policy Update: Password changed successfully!");
        setPasswords({ current: "", new: "", confirm: "" });
      } else {
        toast.error(data.message || "Failed to update password");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Master Admin Profile
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Configure your administrative credentials and security settings.
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: "center", borderRadius: 3 }}>
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <Avatar
                src={getProfilePicUrl()}
                sx={{ width: 120, height: 120, mx: "auto", mb: 2, bgcolor: "primary.main" }}
              >
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="admin-avatar-upload"
                type="file"
                onChange={handlePhotoUpload}
              />
              <label htmlFor="admin-avatar-upload">
                <IconButton
                  color="primary"
                  aria-label="upload picture"
                  component="span"
                  disabled={photoLoading}
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    right: -4,
                    bgcolor: "white",
                    boxShadow: 2,
                    width: 36,
                    height: 36,
                    "&:hover": { bgcolor: "#f5f5f5" },
                  }}
                >
                  {photoLoading ? <CircularProgress size={18} /> : <PhotoCamera sx={{ fontSize: 18 }} />}
                </IconButton>
              </label>
            </Box>
            <Typography variant="h6">{user?.name || "System Admin"}</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {user?.email}
            </Typography>
            <Box mt={2}>
              <Typography variant="caption" sx={{ px: 1, py: 0.5, bgcolor: "error.main", color: "white", borderRadius: 1 }}>
                SUPER ADMIN ACCESS
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <Security color="primary" />
              <Typography variant="h6">Security & Password</Typography>
            </Box>
            <Box component="form" onSubmit={handlePasswordChange}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Admin Password"
                    type="password"
                    value={passwords.current}
                    onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="New Master Password"
                    type="password"
                    value={passwords.new}
                    onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type="password"
                    value={passwords.confirm}
                    onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                  />
                </Grid>
              </Grid>
              <Button
                variant="contained"
                startIcon={<VpnKey />}
                sx={{ mt: 3 }}
                type="submit"
              >
                Update Master Password
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 4, borderRadius: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <Person color="primary" />
              <Typography variant="h6">Administrative Details</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Contact Number" defaultValue={user?.phoneNumber || "N/A"} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Recovery Email" defaultValue={user?.email} />
              </Grid>
            </Grid>
            <Button variant="outlined" startIcon={<Save />} sx={{ mt: 3 }}>
              Save Account Details
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminProfile;
